import React, { memo, useCallback, useState, useMemo } from 'react';
import { Icon, Popup } from 'semantic-ui-react';

/**
 * LitGroupBookListItem component
 * Individual literary group with its books - isolated component for optimal performance
 * Only rerenders when its own data changes or selection state changes
 */
const LitGroupBookListItem = memo(({
  group,
  isSelected,
  isExpanded,
  selectedBookId,
  books,
  onGroupSelect,
  onBookSelect,
  onNewBook,
  onToggleExpand,
  onDeleteBook,
  deletingBookId
}) => {

  // Get the type of a literary group
  const getLitGroupType = useCallback((group) => {
    return group.type && typeof group.type === 'object' ? group.type.name : group.type || 'N/A';
  }, []);

  // Books are already pre-filtered by the parent component
  const groupBooks = books;

  // Handle group selection
  const handleGroupClick = useCallback(() => {
    onGroupSelect(group);
  }, [onGroupSelect, group]);

  // Handle expand/collapse
  const handleToggleExpand = useCallback((e) => {
    e.stopPropagation();
    onToggleExpand(group.uuid);
  }, [onToggleExpand, group.uuid]);

  // Handle book selection
  const handleBookSelect = useCallback((book) => {
    onBookSelect(book, group);
  }, [onBookSelect, group]);

  // Handle new book
  const handleNewBook = useCallback((e) => {
    e.stopPropagation();
    onNewBook(group);
  }, [onNewBook, group]);

  return (
    <div className="bw-litgroup-section">
      {/* Literary Group Header */}
      <div
        className={`bw-list-item-row ${isSelected ? 'selected' : ''}`}
        style={{
          backgroundColor: isSelected ? '#ebf3fb' : 'transparent',
          borderLeft: `4px solid ${isSelected ? '#2185d0' : 'white'}`,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          padding: '8px 12px',
          minHeight: '40px'
        }}
        onClick={handleGroupClick}
      >
        <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <div style={{ marginRight: '10px' }}>
            <Icon
              name={isExpanded ? 'chevron down' : 'chevron right'}
              onClick={handleToggleExpand}
              style={{ cursor: 'pointer' }}
            />
          </div>
          <div style={{ flex: '1', marginRight: '10px' }}>
            {group.name}
          </div>
          <div style={{ marginRight: '15px', minWidth: '80px' }}>
            {getLitGroupType(group)}
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <Popup
              content={`Add new book to ${group.name}`}
              trigger={
                <Icon
                  name="plus circle"
                  color="green"
                  style={{ cursor: 'pointer' }}
                  onClick={handleNewBook}
                />
              }
              position="top center"
              size="tiny"
            />
          </div>
        </div>
      </div>

      {/* Books Section (always rendered, controlled by CSS transitions) */}
      <div
        className="bw-books-section"
        style={{
          marginLeft: '20px',
          maxHeight: isExpanded ? '1000px' : '0',
          overflow: 'hidden',
          transition: 'max-height 0.3s ease-out, opacity 0.2s ease-out',
          opacity: isExpanded ? 1 : 0
        }}
      >
        <div style={{ marginTop: '5px' }}>
          {/* Books List */}
          {groupBooks.length > 0 ? (
            groupBooks.map((book) => (
              <div
                key={book.uuid}
                className={`bw-book-item-row ${selectedBookId === book.uuid ? 'selected' : ''}`}
                style={{
                  backgroundColor: selectedBookId === book.uuid ? '#ebf3fb' : 'transparent',
                  borderLeft: `4px solid ${selectedBookId === book.uuid ? '#2185d0' : 'white'}`,
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '4px',
                  marginBottom: '2px'
                }}
                onClick={() => handleBookSelect(book)}
              >
                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <div style={{ marginRight: '10px' }}>
                    <Icon name="book" color="blue" />
                  </div>
                  <div style={{ flex: '1', marginRight: '10px' }}>
                    {book.profileMissing
                      ? `${book.name || 'Untitled Book'} - No Profile`
                      : (book.name || 'Untitled Book')}
                  </div>
                  <div style={{ marginLeft: 'auto' }}>
                    {deletingBookId === book.uuid ? (
                      <Icon
                        name="spinner"
                        loading
                        color="grey"
                        style={{ cursor: 'not-allowed' }}
                      />
                    ) : (
                      <Popup
                        content={`Delete "${book.name || 'Untitled Book'}"`}
                        trigger={
                          <Icon
                            name="trash alternate"
                            color="red"
                            className="bw-trash-icon"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onDeleteBook) {
                                onDeleteBook(book);
                              }
                            }}
                          />
                        }
                        position="top center"
                        size="tiny"
                      />
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="bw-empty-book-list" style={{
              padding: '10px',
              fontStyle: 'italic',
              color: '#666'
            }}>
              <p>No books in this group</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

// Display name for debugging
LitGroupBookListItem.displayName = 'LitGroupBookListItem';

export default LitGroupBookListItem;
